/** Create / edit a Staffing Request priority. One dialog serves both. */

import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  type StaffingRequestPriorityView,
  useCreateStaffingRequestPriority,
  useUpdateStaffingRequestPriority,
} from "@/hooks/use-staffing-requests";

const schema = z.object({
  name: z.string().trim().min(1, "Name is required").max(60),
  description: z.string().trim().max(300),
});

type FormValues = z.infer<typeof schema>;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  requestPriority?: StaffingRequestPriorityView;
}

export function RequestPriorityEditorDialog({ open, onOpenChange, requestPriority }: Props) {
  const isEdit = Boolean(requestPriority);
  const create = useCreateStaffingRequestPriority();
  const update = useUpdateStaffingRequestPriority();

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: "", description: "" },
  });

  useEffect(() => {
    if (!open) return;
    form.reset({
      name: requestPriority?.name ?? "",
      description: requestPriority?.description ?? "",
    });
  }, [open, requestPriority, form]);

  function onSubmit(values: FormValues) {
    const payload = {
      name: values.name,
      description: values.description.trim() ? values.description : null,
    };
    const onSuccess = () => onOpenChange(false);
    if (requestPriority) {
      update.mutate({ id: requestPriority.id, ...payload }, { onSuccess });
    } else {
      create.mutate(payload, { onSuccess });
    }
  }

  const pending = create.isPending || update.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Request Priority" : "Add Request Priority"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Renaming updates the tag on every request that carries it."
              : "The priority becomes available on Staffing Requests right away, at the bottom of the ranking."}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="High" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea rows={2} placeholder="Optional" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={pending}>
                {pending ? "Saving…" : isEdit ? "Save Changes" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
